import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Save, RefreshCw, ExternalLink, Clock, ShieldCheck,
  AlertTriangle, CheckCircle2, Wrench, Lock, Send, History, X as XIcon,
} from "lucide-react";

const STATUS_META = {
  active:                 { label: "Attiva",              c: "bg-emerald-100 text-emerald-700", I: CheckCircle2 },
  to_configure:           { label: "Da configurare",      c: "bg-amber-100 text-amber-700",     I: Wrench },
  requires_verification:  { label: "Richiede verifica",   c: "bg-orange-100 text-orange-700",   I: AlertTriangle },
  read_only:              { label: "Solo lettura",        c: "bg-slate-200 text-slate-500",     I: Lock },
};
const EDIT_META = {
  medico:  { label: "Medico",  c: "bg-blue-50 text-blue-700 border-blue-200" },
  admin:   { label: "Admin",   c: "bg-purple-50 text-purple-700 border-purple-200" },
  system:  { label: "Sistema", c: "bg-slate-100 text-slate-500 border-slate-200" },
};

function Badge({ meta }) {
  const I = meta.I;
  return (
    <span className={`${meta.c} px-2 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center gap-1`}>
      {I && <I size={11} />} {meta.label}
    </span>
  );
}

function Disclaimer({ registry }) {
  const writable = registry.settings_writable;
  const editable = registry.editable_by;
  if (!writable) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] text-slate-600 flex items-start gap-1.5">
        <Lock size={12} className="mt-0.5 shrink-0" />
        <span>Questa automazione salva solo <b>richieste di modifica</b>. Il comportamento reale non viene modificato da qui.</span>
      </div>
    );
  }
  if (editable === "admin") {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded p-2 text-[11px] text-purple-800 flex items-start gap-1.5">
        <ShieldCheck size={12} className="mt-0.5 shrink-0" />
        <span>Configurazione riservata ad admin PrimeVision.</span>
      </div>
    );
  }
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-800 flex items-start gap-1.5">
      <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
      <span>Questa modifica salva note e metadati. Per il comportamento reale usa il link alla sezione collegata.</span>
    </div>
  );
}

function DetailModal({ item, onClose, onSaved }) {
  const nav = useNavigate();
  const [notes, setNotes] = useState(item.settings?.notes || "");
  const [changeSummary, setChangeSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [crNote, setCrNote] = useState("");
  const [crPriority, setCrPriority] = useState("medium");
  const [crBusy, setCrBusy] = useState(false);

  const canWrite = item.settings_writable && item.editable_by !== "system";

  const loadVersions = useCallback(async () => {
    try {
      const { data } = await api.get(`/automations/versions/${item.key}`);
      setVersions(data);
    } catch (e) { /* silent */ }
  }, [item.key]);

  useEffect(() => { if (showVersions) loadVersions(); }, [showVersions, loadVersions]);

  const save = async () => {
    if (!canWrite) return;
    setBusy(true);
    try {
      await api.put(`/automations/settings/${item.key}`, { notes, change_summary: changeSummary });
      toast.success("Automazione aggiornata");
      setChangeSummary("");
      onSaved?.();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setBusy(false); }
  };

  const restore = async (v) => {
    if (!confirm(`Ripristinare la versione ${v.version_num}? Il valore corrente verrà sovrascritto (creando una nuova versione).`)) return;
    try {
      await api.post(`/automations/versions/${item.key}/restore/${v.id}`);
      toast.success(`Ripristinato dallo stato pre-v${v.version_num}`);
      setShowVersions(false);
      onSaved?.();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const submitCR = async () => {
    if (!crNote.trim()) { toast.error("Descrivi cosa vuoi modificare"); return; }
    setCrBusy(true);
    try {
      await api.post(`/automations/change-requests`, { key: item.key, note: crNote, priority: crPriority });
      toast.success("Richiesta modifica inviata");
      setCrNote("");
    } catch (e) { toast.error(apiErr(e, "Errore invio richiesta")); }
    finally { setCrBusy(false); }
  };

  const openLink = () => {
    if (!item.link_to) return;
    if (item.link_to.startsWith("/")) nav(item.link_to);
    else window.open(item.link_to, "_blank");
    onClose();
  };

  const sMeta = STATUS_META[item.status] || STATUS_META.active;
  const eMeta = EDIT_META[item.editable_by] || EDIT_META.system;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="automation-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-6">
        <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-extrabold text-brand-navy truncate">{item.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{item.category}</div>
          </div>
          <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">v{item.version}</span>
          <button onClick={onClose} data-testid="automation-modal-close"><XIcon size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge meta={sMeta} />
            <span className={`${eMeta.c} px-2 py-0.5 rounded-full text-[10px] font-bold border`}>Modificabile: {eMeta.label}</span>
          </div>

          <div>
            <div className="text-slate-700">{item.description}</div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-50 border border-[#E2E8F0] rounded p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Trigger</div>
                <div className="text-slate-700">{item.trigger || "—"}</div>
              </div>
              <div className="bg-slate-50 border border-[#E2E8F0] rounded p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Azione</div>
                <div className="text-slate-700">{item.action || "—"}</div>
              </div>
            </div>
          </div>

          <Disclaimer registry={item} />

          {canWrite ? (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Note interne</label>
                <textarea
                  value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                  className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1 text-sm resize-none"
                  data-testid="automation-notes"
                  placeholder="Es. attivare solo su glaucoma, disabilitare autoref, ecc."
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Riepilogo modifica</label>
                <input
                  value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 mt-1 text-sm"
                  placeholder="Facoltativo. Es. attivo suggerimento hashtag"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="text-[10px] text-slate-500">Versione salvata: {item.settings?.version_num || 0} · agg. {item.settings?.updated_at ? new Date(item.settings.updated_at).toLocaleString("it-IT") : "—"}</div>
                <div className="flex gap-2">
                  <button onClick={() => setShowVersions((v) => !v)} data-testid="automation-versions-btn" className="text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1"><History size={12} /> Versioni</button>
                  <button onClick={save} disabled={busy} data-testid="automation-save-btn" className="text-xs font-bold bg-brand-navy hover:bg-brand-navyDark disabled:opacity-50 text-white px-3 py-1.5 rounded flex items-center gap-1"><Save size={12} /> Salva/Aggiorna</button>
                </div>
              </div>
              {showVersions && (
                <div className="border border-[#E2E8F0] rounded p-2" data-testid="automation-versions-list">
                  {versions.length === 0 ? <div className="text-xs text-slate-500 italic">Nessuna versione</div> : (
                    <ul className="space-y-1">
                      {versions.map((v) => (
                        <li key={v.id} className="flex items-center gap-2 text-xs border-b border-slate-100 last:border-0 py-1">
                          <span className="font-bold text-brand-navy">v{v.version_num}</span>
                          <span className="text-slate-500 font-mono">{new Date(v.updated_at).toLocaleString("it-IT")}</span>
                          <span className="text-slate-500">{v.updated_by_email}</span>
                          {v.change_summary && <span className="text-slate-600 truncate">— {v.change_summary}</span>}
                          <button onClick={() => restore(v)} className="ml-auto text-[10px] text-brand-navy hover:underline">Ripristina prev.</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="border border-[#E2E8F0] rounded p-3 bg-slate-50">
              <div className="text-xs font-bold text-slate-700 mb-2">Richiedi modifica</div>
              <textarea
                value={crNote} onChange={(e) => setCrNote(e.target.value)} rows={3}
                data-testid="automation-cr-note"
                className="w-full border border-[#E2E8F0] rounded px-2 py-1.5 text-sm resize-none bg-white"
                placeholder="Descrivi cosa vorresti cambiare in questa automazione."
              />
              <div className="mt-2 flex items-center gap-2">
                <select value={crPriority} onChange={(e) => setCrPriority(e.target.value)} className="text-xs border border-[#E2E8F0] rounded px-2 py-1 bg-white">
                  <option value="low">Priorità bassa</option>
                  <option value="medium">Priorità media</option>
                  <option value="high">Priorità alta</option>
                </select>
                <button onClick={submitCR} disabled={crBusy} data-testid="automation-cr-submit" className="text-xs font-bold bg-brand-navy text-white px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50"><Send size={12} /> Invia richiesta</button>
              </div>
            </div>
          )}

          {item.link_to && (
            <button onClick={openLink} data-testid="automation-goto-link" className="w-full text-xs font-bold bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-2 rounded flex items-center justify-center gap-1">
              <ExternalLink size={12} /> Vai alla sezione collegata
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AutomationsCenter({ mode = "medico" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("");
  const [statusF, setStatusF] = useState("");
  const [editF, setEditF] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/automations/registry"); setItems(data); }
    catch (e) { toast.error(apiErr(e, "Errore caricamento")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => Array.from(new Set(items.map((x) => x.category))).sort(), [items]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((x) => {
      if (q && !(`${x.name} ${x.description} ${x.category}`.toLowerCase().includes(q))) return false;
      if (cat && x.category !== cat) return false;
      if (statusF && x.status !== statusF) return false;
      if (editF && x.editable_by !== editF) return false;
      return true;
    });
  }, [items, query, cat, statusF, editF]);

  const byCat = useMemo(() => {
    const g = {};
    filtered.forEach((it) => { (g[it.category] = g[it.category] || []).push(it); });
    return g;
  }, [filtered]);

  return (
    <div data-testid="automations-center">
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs text-slate-700 flex items-start gap-2">
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand-azure" />
        <div>
          {mode === "admin"
            ? "Panoramica automazioni di sistema e globali. Nessun dato clinico dei medici è mostrato qui."
            : "Le tue automazioni Prime Vision. Le modifiche sono salvate e restano nel tempo. Alcune automazioni sono solo lettura e accettano richieste di modifica."}
        </div>
      </div>

      {/* Search + Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
        <label className="relative sm:col-span-2">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
          <input
            data-testid="automation-search"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca automazione…"
            className="w-full border border-[#E2E8F0] rounded px-8 py-1.5 text-sm"
          />
        </label>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="automation-filter-cat" className="border border-[#E2E8F0] rounded px-2 py-1.5 text-sm bg-white">
          <option value="">Tutte le categorie</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="automation-filter-status" className="border border-[#E2E8F0] rounded px-2 py-1.5 text-sm bg-white">
            <option value="">Ogni stato</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={editF} onChange={(e) => setEditF(e.target.value)} data-testid="automation-filter-edit" className="border border-[#E2E8F0] rounded px-2 py-1.5 text-sm bg-white">
            <option value="">Modificabile da…</option>
            {Object.entries(EDIT_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? <div className="text-slate-500 py-8 text-center">Caricamento…</div> : (
        Object.keys(byCat).length === 0 ? <div className="text-slate-500 italic text-sm py-6 text-center">Nessuna automazione corrisponde ai filtri.</div> : (
          <div className="space-y-6" data-testid="automation-groups">
            {Object.entries(byCat).map(([category, group]) => (
              <div key={category}>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">{category}</div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {group.map((it) => {
                    const sMeta = STATUS_META[it.status] || STATUS_META.active;
                    const eMeta = EDIT_META[it.editable_by] || EDIT_META.system;
                    return (
                      <button
                        key={it.key}
                        data-testid={`automation-card-${it.key}`}
                        onClick={() => setActive(it)}
                        className="text-left bg-white border border-[#E2E8F0] rounded-lg p-3 hover:border-brand-azure hover:shadow-sm transition"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-extrabold text-brand-navy leading-snug">{it.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.description}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge meta={sMeta} />
                            <span className={`${eMeta.c} px-2 py-0.5 rounded-full text-[10px] font-bold border`}>{eMeta.label}</span>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-[10px] text-slate-500">
                          <span className="bg-slate-100 px-2 py-0.5 rounded font-mono">v{it.version}</span>
                          {it.settings?.updated_at && <span className="flex items-center gap-0.5"><Clock size={10} /> agg. {new Date(it.settings.updated_at).toLocaleDateString("it-IT")}</span>}
                          {(it.settings?.version_num || 0) > 0 && <span>· {it.settings.version_num} rev.</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {active && <DetailModal item={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />}
    </div>
  );
}
